import { useCallback, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import { Settings, Download, Loader2 } from 'lucide-react';
import { getProviderDisplayName } from './lib/ordering';

export default function TopSellerItemsTable({
  items = [],
  isLoading,
  isLoadingMore,
  hasMoreData,
  onLoadMore,
  totalItems,
}) {
  const [columns, setColumns] = useState(() => [
    {
      id: 'itemName',
      label: 'Item Name',
      width: 200,
      visible: true,
      align: 'left',
      accessor: (item) => item.itemName || 'N/A',
      render: (value) => (
        <div className="font-medium" style={{ whiteSpace: 'nowrap' }}>
          {value}
        </div>
      ),
    },
    {
      id: 'itemCategory',
      label: 'Category',
      width: 150,
      visible: true,
      align: 'left',
      accessor: (item) => item.itemCategory || 'N/A',
      render: (value) => <div style={{ whiteSpace: 'nowrap' }}>{value}</div>,
    },
    {
      id: 'itemType',
      label: 'Type',
      width: 120,
      visible: true,
      align: 'left',
      accessor: (item) => item.itemType || 'N/A',
      render: (value) => <div style={{ whiteSpace: 'nowrap' }}>{value}</div>,
    },
    {
      id: 'totalQuantity',
      label: 'Total Quantity',
      width: 120,
      visible: true,
      align: 'right',
      accessor: (item) => item.totalQuantity || 0,
      render: (value) => <div style={{ whiteSpace: 'nowrap' }}>{value}</div>,
    },
    {
      id: 'totalRevenue',
      label: 'Total Revenue',
      width: 120,
      visible: true,
      align: 'right',
      accessor: (item) => item.totalRevenue || 0,
      render: (value) => (
        <div style={{ whiteSpace: 'nowrap' }}>{formatCurrency(value)}</div>
      ),
    },
    {
      id: 'orderCount',
      label: 'Order Count',
      width: 120,
      visible: true,
      align: 'right',
      accessor: (item) => item.orderCount || 0,
      render: (value) => <div style={{ whiteSpace: 'nowrap' }}>{value}</div>,
    },
    {
      id: 'avgPrice',
      label: 'Avg Price',
      width: 100,
      visible: true,
      align: 'right',
      accessor: (item) => item.avgPrice || 0,
      render: (value) => (
        <div style={{ whiteSpace: 'nowrap' }}>{formatCurrency(value)}</div>
      ),
    },
    {
      id: 'providers',
      label: 'Providers',
      width: 150,
      visible: false,
      align: 'left',
      accessor: (item) => item.providers || [],
      render: (value) => (
        <div className="flex flex-wrap gap-1">
          {value.length > 0 ? (
            value.map((provider) => (
              <Badge key={provider} variant="outline">
                {getProviderDisplayName(provider)}
              </Badge>
            ))
          ) : (
            <span className="text-muted-foreground">-</span>
          )}
        </div>
      ),
    },
  ]);

  const formatCurrency = useCallback((amount) => {
    const normalized = Object.is(amount, -0) ? 0 : amount;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(normalized);
  }, []);

  const visibleColumns = columns.filter((col) => col.visible);

  const toggleColumnVisibility = useCallback((columnId) => {
    setColumns((prev) =>
      prev.map((col) =>
        col.id === columnId ? { ...col, visible: !col.visible } : col
      )
    );
  }, []);

  const handleExportCSV = useCallback(() => {
    if (!items || items.length === 0) return;

    // Get visible columns
    const visibleColumns = columns.filter((col) => col.visible);

    // Create CSV header
    const headers = visibleColumns.map((col) => col.label).join(',');

    // Create CSV rows
    const rows = items.map((item) => {
      return visibleColumns
        .map((col) => {
          let value = col.accessor(item);

          // Format the value based on column type
          if (col.id === 'providers' && Array.isArray(value)) {
            value = value.map((p) => getProviderDisplayName(p)).join('; ');
          } else if (
            typeof value === 'number' &&
            col.id !== 'orderCount' &&
            col.id !== 'totalQuantity'
          ) {
            // Format numbers with 2 decimal places for currency and percentages
            value = value.toFixed(2);
          }

          // Escape commas and quotes in CSV
          if (typeof value === 'string') {
            value = value.replace(/"/g, '""');
            if (
              value.includes(',') ||
              value.includes('"') ||
              value.includes('\n')
            ) {
              value = `"${value}"`;
            }
          }

          return value || '';
        })
        .join(',');
    });

    // Combine header and rows
    const csv = [headers, ...rows].join('\n');

    // Create and trigger download
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    const timestamp = new Date().toISOString().slice(0, 10);
    link.setAttribute('href', url);
    link.setAttribute('download', `top-seller-items-${timestamp}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [columns, items]);

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <CardTitle>Top Seller Items</CardTitle>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 w-full sm:w-auto">
            {/* Column Visibility */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full sm:w-auto"
                >
                  <Settings className="h-4 w-4 mr-2" />
                  Columns
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel>Toggle Columns</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {columns.map((column) => (
                  <DropdownMenuCheckboxItem
                    key={column.id}
                    checked={column.visible}
                    onCheckedChange={() => toggleColumnVisibility(column.id)}
                  >
                    {column.label}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Export to CSV */}
            <Button
              variant="outline"
              size="sm"
              className="w-full sm:w-auto"
              onClick={handleExportCSV}
              disabled={!items || items.length === 0}
            >
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        {/* Table Container with Horizontal Scroll */}
        <div className="border rounded-md overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {visibleColumns.map((column) => (
                    <TableHead
                      key={column.id}
                      style={{
                        width: column.width,
                        minWidth: column.width,
                        maxWidth: column.width,
                        whiteSpace: 'nowrap',
                        padding: '8px 12px',
                      }}
                      className={
                        column.align === 'right' ? 'text-right' : 'text-left'
                      }
                    >
                      {column.label}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading && items.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={visibleColumns.length}
                      className="text-center py-8"
                    >
                      <div className="flex items-center justify-center">
                        <Loader2 className="h-6 w-6 animate-spin mr-2" />
                        <span>Loading...</span>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : items.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={visibleColumns.length}
                      className="text-center py-8"
                    >
                      No items found
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((item, index) => (
                    <TableRow
                      key={item.itemUuid || index}
                      className={`hover:bg-muted/70 transition-colors ${
                        index % 2 === 0 ? 'bg-background' : 'bg-muted/20'
                      }`}
                    >
                      {visibleColumns.map((column) => {
                        const value = column.accessor(item);
                        const content = column.render
                          ? column.render(value, item)
                          : value;

                        return (
                          <TableCell
                            key={column.id}
                            style={{
                              width: column.width,
                              minWidth: column.width,
                              maxWidth: column.width,
                              padding: '8px 12px',
                            }}
                            className={
                              column.align === 'right'
                                ? 'text-right'
                                : 'text-left'
                            }
                          >
                            {content}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        {/* Info and Load More Section */}
        <div className="flex flex-col items-center gap-4 mt-4">
          <div className="text-sm text-muted-foreground">
            Showing {items.length} of {totalItems || items.length} items
          </div>

          {/* Load More Button */}
          {hasMoreData && (
            <Button
              onClick={onLoadMore}
              disabled={isLoadingMore}
              variant="outline"
              className="w-full sm:w-auto"
            >
              {isLoadingMore ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Loading...
                </>
              ) : (
                'Load More'
              )}
            </Button>
          )}
        </div>

        {/* Performance Stats */}
        <div className="mt-4 p-3 bg-muted/30 rounded-lg">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-xs text-muted-foreground">
            <span>
              📊 {visibleColumns.length} of {columns.length} columns visible
            </span>
            <span>📦 {items.length} items loaded</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
